import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import "flatpickr/dist/themes/material_blue.css";
import { isEmpty, map } from "lodash";
import DeleteModal from "../../components/Common/DeleteModal";
import SettingSidebar from "../../components/VerticalLayout/SettingSidebar";
import {
  addLeadStage,
  getLeadStage,
  deleteLeadStage,
  updateLeadStage,
} from "../../store/actions";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";
import SafeField from "helpers/SafeField";

const LeadStage = props => {
  document.title = "General Settings | Lead Stage";

  const dispatch = useDispatch();
 

  const { leadstages, loading } = useSelector(state => ({
    leadstages: state.ObjectReducer.leadstages,
    loading: state.ObjectReducer.loading,
  }));

  const [leadstage, setLeadStage] = useState({});
  const [isRight, setIsRight] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  /* -------------------- API Calls -------------------- */

  useEffect(() => {
    dispatch(getLeadStage(localStorage.getItem("userId")));
  }, [dispatch]);

  useEffect(() => {
    if (!isEmpty(leadstages)) {
      setIsEdit(false);
    }
  }, [leadstages]);

  /* -------------------- Handlers -------------------- */

  const toggle = () => setIsRight(!isRight);

  const toggleRightCanvas = selected => {
    if (selected) {
      setLeadStage({
        id: selected.id,
        leadStageTitle: selected.leadStageTitle,
        agentId: selected.agentId,
      });
      setIsEdit(true);
    } else {
      setLeadStage({});
      setIsEdit(false);
    }
    toggle();
  };

  const handleLeadStageSubmit = values => {
    dispatch(addLeadStage(values, history));
  };

  const onClickDelete = selected => {
    setLeadStage(selected);
    setDeleteModal(true);
  };

  const handleDeleteLeadStage = () => {
    if (leadstage?.id) {
      dispatch(deleteLeadStage(leadstage));
      setDeleteModal(false);
    }
  };

  /* -------------------- Render -------------------- */

  return (
    <>
      {loading && <div className="loading">Loading…</div>}
      <ToastContainer autoClose={2000} />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteLeadStage}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
      
        <Container fluid>
          <Row className="mb20">
            <Col xl="12">
              <Breadcrumbs
                title={props.t("General Settings")}
                breadcrumbItem={props.t("Lead Stage")}
              />
            </Col>
          </Row>

          <Row>
           
            <Col xl="12">
              {/* ---------------- Add Form ---------------- */}
              <Formik
                enableReinitialize
                initialValues={{ leadStageTitle: "" }}
                validationSchema={Yup.object({
                  leadStageTitle: Yup.string().required("This is required"),
                })}
                onSubmit={handleLeadStageSubmit}
              >
                {({ errors, touched, handleChange }) => (
                  <Form className="needs-validation">
                    <Card>
                      <CardBody>
                        <Row>
                          <Col sm="3">
                            <FormGroup className="mb-3">
                              <Label>Title *</Label>
                              <SafeField
                                name="leadStageTitle"
                                onChange={handleChange}
                                type="text"
                                className={
                                  "form-control" +
                                  (errors.leadStageTitle &&
                                  touched.leadStageTitle
                                    ? " is-invalid"
                                    : "")
                                }
                              />
                              <ErrorMessage
                                name="leadStageTitle"
                                component="div"
                                className="invalid-feedback"
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="2">
                            <button
                              type="submit"
                              className="btn btn-primary w-md"
                              style={{ marginTop: 31 }}
                            >
                              Add
                            </button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* ---------------- History Table ---------------- */}
                    <Card>
                      <CardBody>
                        <h5 className="card-title">History</h5>
                        <div className="table-responsive">
                          <table className="table table-bordered align-middle table-nowrap mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {map(leadstages, (item, index) => (
                                <tr key={index}>
                                  <th>{index + 1}</th>
                                  <td>{item.leadStageTitle}</td>
                                  <td>
                                    {item.agentId > 0 && (
                                      <>
                                        <ActionEdit
                                          onClick={() =>
                                            toggleRightCanvas(item)
                                          }
                                        />
                                        <ActionDelete
                                          onClick={() => onClickDelete(item)}
                                        />
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Form>
                )}
              </Formik>
            </Col>
          </Row>

          {/* ---------------- Update Offcanvas ---------------- */}
          <Offcanvas isOpen={isRight} direction="end" toggle={toggle}>
            <OffcanvasHeader toggle={toggle}>Update</OffcanvasHeader>
            <OffcanvasBody>
              <Formik
                enableReinitialize
                initialValues={{
                  id: leadstage?.id,
                  leadStageTitle: leadstage?.leadStageTitle || "",
                  agentId: leadstage?.agentId,
                }}
                validationSchema={Yup.object({
                  leadStageTitle: Yup.string().required("This is required"),
                })}
                onSubmit={values => {
                  if (isEdit) {
                    dispatch(updateLeadStage(values));
                  }
                  toggle();
                }}
              >
                {({ errors, touched, handleChange }) => (
                  <Form className="needs-validation">
                    <Row>
                      <Col sm="12">
                        <FormGroup className="mb-3">
                          <Label>Title *</Label>
                          <SafeField
                            name="leadStageTitle"
                            onChange={handleChange}
                            type="text"
                            className={
                              "form-control" +
                              (errors.leadStageTitle &&
                              touched.leadStageTitle
                                ? " is-invalid"
                                : "")
                            }
                          />
                          <ErrorMessage
                            name="leadStageTitle"
                            component="div"
                            className="invalid-feedback"
                          />
                        </FormGroup>
                      </Col>

                      <Col sm="12">
                        <button
                          type="submit"
                          className="btn btn-primary w-md"
                        >
                          Update
                        </button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </OffcanvasBody>
          </Offcanvas>
        </Container>
      </div>
    </>
  );
};

LeadStage.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(LeadStage);
